import { useMemo } from "react"
import { useQuery } from "urql"

import type {
  GetPriceChangeRecipientsInput,
  GetPriceChangeRecipientsQuery,
  PriceChangeRecipient,
} from "@/graphql/codegen/graphql"
import { GetPriceChangeRecipientsDocument } from "@/graphql/codegen/graphql"

interface UseGetPriceChangeRecipientsReturn {
  recipients?: PriceChangeRecipient[]
  customers?: {
    companyId: string
    companyName: string
  }[]
  contacts?: {
    contactId: string
    contactName: string
  }[]
  totalAccounts: number
  totalContacts: number
  isFetching: boolean
  refetch: () => void
}

export const useGetPriceChangeRecipients = (
  input: GetPriceChangeRecipientsInput | null
): UseGetPriceChangeRecipientsReturn => {
  const [{ data, fetching }, reexecuteQuery] = useQuery<GetPriceChangeRecipientsQuery>({
    query: GetPriceChangeRecipientsDocument,
    variables: { input },
    pause: !input?.customerIds?.length,
  })

  const refetch = () => reexecuteQuery({ requestPolicy: "network-only" })

  const { recipients, totalAccounts, contacts, customers, totalContacts } = useMemo(() => {
    if (!input) {
      return { recipients: undefined, contacts: undefined, customers: undefined, totalAccounts: 0, totalContacts: 0 }
    }

    const output = data?.priceChange?.getPriceChangeRecipients

    if (output?.__typename === "GetPriceChangeRecipientsSuccess") {
      const customers = [
        ...new Map(
          output?.recipients?.map(({ companyId, companyName }) => [companyId, { companyId, companyName }])
        ).values(),
      ]

      const contacts = [
        ...new Map(
          output.recipients.map(({ contactId, contactName }) => [contactId, { contactId, contactName }])
        ).values(),
      ]

      return {
        recipients: output.recipients,
        customers: customers,
        contacts: contacts,
        totalAccounts: output.totalAccounts,
        totalContacts: output.totalContacts,
      }
    }
    return {
      recipients: [] as PriceChangeRecipient[],
      customers: [],
      contacts: [],
      totalAccounts: 0,
      totalContacts: 0,
    }
  }, [data, input])

  return {
    recipients,
    contacts,
    customers,
    totalAccounts,
    totalContacts,
    isFetching: fetching,
    refetch,
  }
}
